import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/accents";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/auth/reset-password/${token}`, { password });
      setUser(data);
      toast.success("Password updated — you're signed in.");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const firstName = info?.first_name || "";

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-md fade-up">
        <img src={LOGO_URL} alt="UnCloned®" className="h-9 w-auto" />
        {error ? (
          <div className="mt-10" data-testid="reset-error">
            <h2 className="font-display font-black text-3xl text-[#000000]">Reset link unavailable</h2>
            <p className="text-gray-500 mt-3 text-sm">{error}</p>
            <Link
              to="/forgot-password"
              data-testid="request-new-link"
              className="inline-block mt-6 text-sm font-bold text-[#DF1283] hover:underline"
            >
              Request a new reset link
            </Link>
          </div>
        ) : !info ? (
          <div className="mt-10 text-gray-400">Checking your link...</div>
        ) : (
          <>
            <h2 className="font-display font-black text-3xl text-[#000000] mt-10" data-testid="reset-hello">
              {firstName ? `Hi ${firstName},` : "Reset your password"}
            </h2>
            <p className="text-gray-500 mt-2 text-sm">
              Choose a new password for <span className="font-bold text-[#000000]">{info.email}</span>. You&rsquo;ll be signed in automatically once you save.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">New password</label>
                <input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Confirm password</label>
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat password"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                />
              </div>
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#D0FF0D] text-[#000000] rounded-full px-6 py-3 font-bold text-sm hover:bg-[#0FFFE2] transition-colors duration-200 disabled:opacity-60"
              >
                {loading ? "Saving..." : "Save password & sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
